import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LocalDate } from "@/components/ui/local-date";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/projects/:slug/members` — the project's member roster. Each member holds
 * one or more Hub roles (what they do in the project) and, separately, their
 * GitLab access to the repository, which Hub pushes to GitLab.
 */

type ProjectRole = "manager" | "developer" | "tester" | "maintainer" | "client";
type GitlabAccess = "guest" | "planner" | "reporter" | "developer" | "maintainer" | "owner";

const ALL_PROJECT_ROLES: ProjectRole[] = ["manager", "developer", "tester", "maintainer", "client"];

const GITLAB_ACCESS: GitlabAccess[] = [
  "guest",
  "planner",
  "reporter",
  "developer",
  "maintainer",
  "owner",
];

type MemberUser = { id: string; name: string | null; email: string | null };

type Member = {
  id: string;
  roles: ProjectRole[];
  gitlabAccess: GitlabAccess | null;
  createdAt: string;
  userId: string;
  user: MemberUser;
  /** An agent: membership, role and access are fixed by Hub. */
  fixed: boolean;
};

type MembersResponse = {
  members: Member[];
  canManage: boolean;
  availableUsers: MemberUser[];
};

function RoleChips({
  value,
  onToggle,
  disabled,
}: {
  value: ProjectRole[];
  onToggle?: (role: ProjectRole) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (!onToggle) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((r) => (
          <Badge key={r} variant={r === "manager" ? "default" : "secondary"}>
            {t(`projMembers.roles.${r}`)}
          </Badge>
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {ALL_PROJECT_ROLES.map((r) => {
        const on = value.includes(r);
        return (
          <button
            key={r}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(r)}
            className={`rounded-full border px-2 py-0.5 text-xs transition-colors disabled:opacity-50 ${
              on
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input text-muted-foreground hover:bg-accent"
            }`}
          >
            {t(`projMembers.roles.${r}`)}
          </button>
        );
      })}
    </div>
  );
}

const toggle = (roles: ProjectRole[], r: ProjectRole) =>
  roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];

export const Route = createFileRoute("/_app/projects/$slug/members")({
  loader: ({ params }) => api.get<MembersResponse>(`/api/projects/${params.slug}/members/manage`),
  component: ProjectMembersPage,
});

function ProjectMembersPage() {
  const { members, canManage, availableUsers } = Route.useLoaderData();
  const params = Route.useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const accessOptions: Array<{ value: GitlabAccess | ""; label: string }> = [
    { value: "", label: t("projMembers.access.none") },
    ...GITLAB_ACCESS.map((a) => ({ value: a, label: t(`projMembers.access.${a}`) })),
  ];

  const [addUserId, setAddUserId] = useState("");
  const [addRoles, setAddRoles] = useState<ProjectRole[]>(["developer"]);
  const [addAccess, setAddAccess] = useState<GitlabAccess | "">("developer");
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [alsoGitlab, setAlsoGitlab] = useState(false);

  async function run(key: string, title: string, fn: () => Promise<unknown>) {
    setBusy(key);
    try {
      await fn();
      await router.invalidate();
    } catch (err) {
      toast({
        title,
        description: err instanceof Error ? err.message : t("projMembers.error"),
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  }

  const addMember = () =>
    run("add", t("projMembers.addFailed"), async () => {
      await api.post(`/api/projects/${params.slug}/members`, {
        userId: addUserId,
        roles: addRoles,
        gitlabAccess: addAccess || null,
      });
      setAddUserId("");
      setAddRoles(["developer"]);
      setAddAccess("developer");
    });

  const patchMember = (member: Member, body: Record<string, unknown>) =>
    run(member.id, t("projMembers.changeFailed"), () =>
      api.patch(`/api/projects/${params.slug}/members/${member.id}`, body),
    );

  const askRemove = (member: Member) => {
    setAlsoGitlab(false);
    setRemoving(member);
  };

  const removeMember = async () => {
    const member = removing;
    if (!member) return;
    setRemoving(null);
    await run(member.id, t("projMembers.removeFailed"), () =>
      api.delete(
        `/api/projects/${params.slug}/members/${member.id}${alsoGitlab ? "?removeFromGitlab=1" : ""}`,
      ),
    );
  };

  const columns: Array<DataTableColumn<Member>> = [
    {
      key: "name",
      header: t("projMembers.name"),
      sortAccessor: (m) => m.user.name,
      cellClassName: "font-medium",
      cell: (m) => m.user.name,
    },
    {
      key: "email",
      header: t("projMembers.email"),
      sortAccessor: (m) => m.user.email,
      cellClassName: "text-muted-foreground text-sm",
      cell: (m) => m.user.email,
    },
    {
      key: "roles",
      header: t("projMembers.rolesHeader"),
      sortAccessor: (m) => m.roles.join(","),
      cell: (m) =>
        canManage && !m.fixed ? (
          <RoleChips
            value={m.roles}
            disabled={busy === m.id}
            onToggle={(r) => {
              const roles = toggle(m.roles, r);
              if (roles.length) patchMember(m, { roles });
            }}
          />
        ) : (
          <RoleChips value={m.roles} />
        ),
    },
    {
      key: "gitlab",
      header: t("projMembers.gitlabAccess"),
      sortAccessor: (m) => m.gitlabAccess ?? "",
      cell: (m) =>
        canManage && !m.fixed ? (
          <Select
            aria-label={t("projMembers.gitlabAccess")}
            size="sm"
            value={m.gitlabAccess ?? ""}
            disabled={busy === m.id}
            onChange={(v) => patchMember(m, { gitlabAccess: v || null })}
            options={accessOptions}
          />
        ) : (
          <span className="text-sm capitalize text-muted-foreground">
            {t(`projMembers.access.${m.gitlabAccess ?? "none"}`)}
          </span>
        ),
    },
    {
      key: "joined",
      header: t("projMembers.joined"),
      sortAccessor: (m) => m.createdAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (m) => <LocalDate value={m.createdAt} />,
    },
    {
      key: "actions",
      header: "",
      hidden: !canManage,
      headClassName: "w-[90px]",
      cell: (member) =>
        member.fixed ? (
          <span className="text-xs text-muted-foreground">{t("projMembers.fixed")}</span>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="h-8 text-xs px-2"
            disabled={busy === member.id}
            onClick={() => askRemove(member)}
          >
            {t("projMembers.remove")}
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">{t("projMembers.title")}</h2>
        <span className="text-sm text-muted-foreground">
          {t("projMembers.count", { count: members.length })}
        </span>
      </div>

      {canManage && availableUsers.length > 0 && (
        <div className="rounded-md border bg-card p-5">
          <h3 className="font-heading text-sm font-semibold mb-3">{t("projMembers.addTitle")}</h3>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t("projMembers.user")}
              </span>
              <Combobox
                value={addUserId}
                onChange={setAddUserId}
                placeholder={t("projMembers.selectUser")}
                emptyLabel={t("projMembers.noMoreUsers")}
                options={availableUsers.map((u) => ({
                  value: u.id,
                  label: u.name ?? u.email ?? u.id,
                  ...(u.email ? { hint: u.email } : {}),
                }))}
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                {t("projMembers.rolesHeader")}
              </span>
              <RoleChips value={addRoles} onToggle={(r) => setAddRoles((v) => toggle(v, r))} />
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                {t("projMembers.gitlabAccess")}
              </span>
              <Select
                aria-label={t("projMembers.gitlabAccess")}
                value={addAccess}
                onChange={(v) => setAddAccess(v as GitlabAccess | "")}
                options={accessOptions}
              />
            </div>

            <Button
              type="button"
              size="sm"
              onClick={addMember}
              disabled={!addUserId || addRoles.length === 0 || busy === "add"}
            >
              {t("projMembers.addButton")}
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={members}
        rowKey={(m) => m.id}
        emptyState={t("projMembers.empty")}
      />
      <Dialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("projMembers.removeDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("projMembers.removeDialog.description", { name: removing?.user.name ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={alsoGitlab}
              onChange={(e) => setAlsoGitlab(e.target.checked)}
            />
            {t("projMembers.removeDialog.alsoGitlab")}
          </label>
          <p className="text-xs text-muted-foreground">{t("projMembers.removeDialog.hint")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)}>
              {t("projMembers.removeDialog.cancel")}
            </Button>
            <Button variant="destructive" onClick={removeMember}>
              {t("projMembers.removeDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
